import { Dialog, DialogTitle, Box, FormControl, FormControlLabel, FormLabel, Radio, RadioGroup, DialogContent, DialogActions, Button, List, ListItem, ListItemText, TextField, InputAdornment, Divider } from '@mui/material';
import React, { useEffect } from 'react';
import { ExpenseDivisionMethod, SHARED_PERSON_KEY } from './AddExpenses';

interface Props {
  open: boolean,
  onClose: () => void,
  expenseTotal: number,
  peopleOnThisExpense: string[],
  expenseDivisionMethod: ExpenseDivisionMethod,
  expenseDivisionPersonToAmountMap: Record<string, number>,
  onSubmit(expenseDivisionMethod: ExpenseDivisionMethod, expenseDivisionPersonToAmountMap: Record<string, number>): void,
}

export default function ExpenseDivisionMethodDialog(props: Props) {
  const [localExpenseDivisionMethod, setLocalExpenseDivisionMethod] = React.useState<ExpenseDivisionMethod>(props.expenseDivisionMethod);
  const [localExpenseDivisionPersonToAmountMap, setLocalExpenseDivisionPersonToAmountMap] = React.useState<Record<string, number>>(props.expenseDivisionPersonToAmountMap);

  function formattedAsMoneyWithThreeSigFigs(amount: number) {
    // Return a maximum of 3 decimals, but a minimum of 2.
    // Only return 3 decimals if the last digit is not a zero.

    const asString = amount.toFixed(3);
    const asStringWithTwoDecimals = amount.toFixed(2);

    return asString.endsWith('0') ? asStringWithTwoDecimals : asString;
  }

  // Initialize local state when the dialog opens
  useEffect(() => {
    if (props.open) {
      setLocalExpenseDivisionMethod(props.expenseDivisionMethod);
      setLocalExpenseDivisionPersonToAmountMap(props.expenseDivisionPersonToAmountMap);
    }
  }, [props.open]);

  useEffect(() => {
    if (localExpenseDivisionMethod === ExpenseDivisionMethod.Equal) {
      const equalAmount = props.expenseTotal / props.peopleOnThisExpense.length;
      const updatedMap = Object.fromEntries(props.peopleOnThisExpense.map(person => [person, equalAmount]));
      setLocalExpenseDivisionPersonToAmountMap(updatedMap);
    } else if (localExpenseDivisionMethod === ExpenseDivisionMethod.Manual) {
      const updatedMap = Object.fromEntries(props.peopleOnThisExpense.map(person => [person, 0]));
      updatedMap[SHARED_PERSON_KEY] = 0;
      setLocalExpenseDivisionPersonToAmountMap(updatedMap);
    }
  }, [localExpenseDivisionMethod, props.expenseTotal, props.peopleOnThisExpense]);

  const handleAmountChange = (person: string, amount: string) => {
    setLocalExpenseDivisionPersonToAmountMap(prev => ({
      ...prev,
      [person]: amount as unknown as number
    }));
  };

  const personToAmountMapTotalDelta = Object.values(localExpenseDivisionPersonToAmountMap).reduce((sum, amount) => sum + Number(amount), 0) - props.expenseTotal;

  const readyToSubmit = localExpenseDivisionMethod === ExpenseDivisionMethod.Equal || (
    Object.values(localExpenseDivisionPersonToAmountMap).every(amount => !isNaN(amount)) &&
    personToAmountMapTotalDelta === 0
  );

  return (
    <Dialog open={props.open} onClose={props.onClose} fullWidth>
      <DialogTitle>How is this expense being divided?</DialogTitle>
      <DialogContent>
        <FormControl>
          <RadioGroup
            value={localExpenseDivisionMethod || ExpenseDivisionMethod.Equal}
            onChange={(e) => setLocalExpenseDivisionMethod(e.target.value as ExpenseDivisionMethod)}
          >
            <FormControlLabel value={ExpenseDivisionMethod.Equal} control={<Radio />} label="Split equally." />
            <FormControlLabel value={ExpenseDivisionMethod.Manual} control={<Radio />} label="Each person owes a specific amount." />
          </RadioGroup>
        </FormControl>

        {localExpenseDivisionMethod === ExpenseDivisionMethod.Manual &&
          personToAmountMapTotalDelta !== 0 &&
          <Box color="error.main">
            {
              personToAmountMapTotalDelta < 0 ?
                `Amount remaining: $${formattedAsMoneyWithThreeSigFigs(-personToAmountMapTotalDelta)}` :
                `Amount overpaid: $${formattedAsMoneyWithThreeSigFigs(personToAmountMapTotalDelta)}`
            }
          </Box>
        }

        {/* List of people with text fields for amount input */}
        <List>
          {localExpenseDivisionMethod === ExpenseDivisionMethod.Manual && (<>
            <ListItem>
              <ListItemText primary="Shared cost" secondary="Tips, taxes, appetizers, etc" />
              <TextField
                type="number"
                InputProps={{
                  startAdornment: <InputAdornment position="start">
                    $
                  </InputAdornment>,
                }}
                value={localExpenseDivisionPersonToAmountMap[SHARED_PERSON_KEY]}
                onChange={(e) => handleAmountChange(SHARED_PERSON_KEY, e.target.value)}
                size="small"
                sx={{
                  minWidth: "120px",
                  width: "120px",
                  ml: 1
                }}
              />
            </ListItem>
            <Divider sx={{ mb: 1 }} />
          </>)}
          {props.peopleOnThisExpense.map(person => (
            <ListItem key={person}>
              <ListItemText primary={person} />
              <TextField
                type="number"
                InputProps={{
                  startAdornment: <InputAdornment position="start">
                    $
                  </InputAdornment>,
                }}
                value={
                  localExpenseDivisionMethod === ExpenseDivisionMethod.Equal ?
                    formattedAsMoneyWithThreeSigFigs(props.expenseTotal / props.peopleOnThisExpense.length) :
                    localExpenseDivisionPersonToAmountMap[person]}
                onChange={(e) => handleAmountChange(person, e.target.value)}
                disabled={localExpenseDivisionMethod === ExpenseDivisionMethod.Equal}
                size="small"
                sx={{
                  minWidth: "120px",
                  width: "120px",
                  ml: 1
                }}
              />
            </ListItem>
          ))}
        </List>
      </DialogContent>
      <DialogActions>
        <Box flexGrow={1} />
        <Button onClick={props.onClose}>Cancel</Button>
        <Button
          onClick={() => props.onSubmit(localExpenseDivisionMethod, localExpenseDivisionPersonToAmountMap)}
          disabled={!readyToSubmit}
        >
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
}
